import { AggregationsContainer } from '@metrics/AggregationsContainer';
import {
  TAggregatedFieldConfiguration,
  TAggregatedFieldsConfiguration,
  TAggregatedFieldsV2,
  TCalculatedValueFields,
} from '@metrics/IAggregatedMetricsV2';

const AGGREGATED_FIELDS_CONFIGURATION: TAggregatedFieldsConfiguration = {
  Latency: [
    {
      scope: 'report',
      aggregates: ['max', 'min', 'avg', 'median'],
    },
    {
      scope: 'session',
      aggregates: ['max', 'min', 'avg'],
    },
  ],
};

const metricsPrefixes: Record<'report' | 'session', string> = {
  report: '',
  session: 'Session',
};

export class AggregatedMetricsV2 {
  private readonly _sessionAggregatedFields: Partial<
    Record<TAggregatedFieldsV2, AggregationsContainer>
  > = {};
  private readonly _reportAggregatedFields: Partial<
    Record<TAggregatedFieldsV2, AggregationsContainer>
  > = {};

  constructor() {
    Object.entries(AGGREGATED_FIELDS_CONFIGURATION).forEach(([fieldName, configs]) => {
      for (const config of configs) {
        if (config.scope === 'session') {
          this._sessionAggregatedFields[fieldName as TAggregatedFieldsV2] =
            new AggregationsContainer(config.aggregates);
        } else {
          this._reportAggregatedFields[fieldName as TAggregatedFieldsV2] =
            new AggregationsContainer(config.aggregates);
        }
      }
    });
  }

  public addValue(field: TAggregatedFieldsV2, value: number): void {
    this._sessionAggregatedFields[field]?.addValue(value);
    this._reportAggregatedFields[field]?.addValue(value);
  }

  public addValues(field: TAggregatedFieldsV2, values: number[]): void {
    this._sessionAggregatedFields[field]?.addValues(values);
    this._reportAggregatedFields[field]?.addValues(values);
  }

  public clearValues(field: TAggregatedFieldsV2): void {
    this._sessionAggregatedFields[field]?.clear();
    this._reportAggregatedFields[field]?.clear();
  }

  public clearReportValues(): void {
    Object.values(this._reportAggregatedFields).forEach((field) => field.clear());
  }

  public clearSessionValues(): void {
    Object.values(this._sessionAggregatedFields).forEach((field) => field.clear());
  }

  public clearAllValues(): void {
    this.clearReportValues();
    this.clearSessionValues();
  }

  public getAllValues(): Partial<Record<TCalculatedValueFields, number>> {
    const result: Partial<Record<TCalculatedValueFields, number>> = {
      ...this._populateValuesByScope('session'),
      ...this._populateValuesByScope('report'),
    };

    this.clearReportValues();

    return result;
  }

  private _populateValuesByScope(
    requiredScope: 'session' | 'report',
  ): Partial<Record<TCalculatedValueFields, number>> {
    const result: Partial<Record<TCalculatedValueFields, number>> = {};

    const container: Partial<Record<TAggregatedFieldsV2, AggregationsContainer>> =
      requiredScope === 'session' ? this._sessionAggregatedFields : this._reportAggregatedFields;

    Object.keys(container).forEach((key: TAggregatedFieldsV2) => {
      const fieldConfig: TAggregatedFieldConfiguration | undefined =
        AGGREGATED_FIELDS_CONFIGURATION[key].find(({ scope }) => scope === requiredScope);

      if (!fieldConfig) {
        return;
      }

      const aggregates: TAggregatedFieldConfiguration['aggregates'] = fieldConfig.aggregates;

      for (const aggregate of aggregates) {
        if (container[key] && container[key]?.[aggregate] !== null)
          result[`${aggregate}${key}${metricsPrefixes[requiredScope]}`] = container[key]?.[
            aggregate
          ] as number;
      }
    });

    return result;
  }
}
